import { Link, NavLink, Outlet } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { LogOut } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useSiteContent } from "../lib/content";
import { fetchWhatsappQr, fetchWhatsappStatus } from "../lib/whatsappApi";
import { Button } from "../components/ui/Button";
import { ThemeToggle } from "../components/ThemeToggle";
import { cn } from "../lib/utils";

const links = [
  { to: "/admin", label: "Pedidos", end: true },
  { to: "/admin/inicio", label: "Início", end: false },
  { to: "/admin/servicos", label: "Serviços", end: false },
  { to: "/admin/como-funciona", label: "Como funciona", end: false },
  { to: "/admin/contactos", label: "Contactos", end: false },
];

export function AdminLayout() {
  const { signOut, user } = useAuth();
  const { missingTable } = useSiteContent();
  const [wa, setWa] = useState<{ status: string; connected: boolean } | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);

  const refreshWa = useCallback(async () => {
    try {
      setWa(await fetchWhatsappStatus());
    } catch {
      setWa({ status: "UNREACHABLE", connected: false });
    }
  }, []);

  useEffect(() => {
    void refreshWa();
    const id = window.setInterval(() => void refreshWa(), 60_000);
    return () => window.clearInterval(id);
  }, [refreshWa]);

  const openQr = async () => {
    setQrOpen(true);
    setQrLoading(true);
    setQrError(null);
    setQrDataUrl(null);
    try {
      const { dataUrl } = await fetchWhatsappQr();
      setQrDataUrl(dataUrl);
    } catch (err) {
      setQrError(err instanceof Error ? err.message : "Erro ao obter QR");
    } finally {
      setQrLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {wa && !wa.connected && (
        <div className="border-b border-urgent/40 bg-urgent/10 px-4 py-3 text-sm text-urgent">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
            <p>
              WhatsApp desconectado ({wa.status}) — os clientes não recebem confirmação automática.
            </p>
            <Button size="sm" onClick={() => void openQr()}>
              Religar (QR)
            </Button>
          </div>
        </div>
      )}
      <header className="border-b bg-surface/80">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-4">
          <div>
            <p className="font-heading font-semibold text-primary">Painel admin</p>
            <p className="text-xs text-muted">{user?.email}</p>
            {wa && (
              <p
                className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                  wa.connected ? "bg-accent-terminal/15 text-accent-terminal" : "bg-urgent/15 text-urgent"
                }`}
              >
                {wa.connected ? "WhatsApp OK" : "WhatsApp off"}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link to="/" className="text-sm text-muted hover:text-primary">
              Ver site
            </Link>
            <Button variant="ghost" size="sm" onClick={() => void signOut()}>
              <LogOut className="h-4 w-4" aria-hidden />
              Sair
            </Button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-6xl flex-wrap gap-1 px-4 pb-3" aria-label="Secções do admin">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) =>
                cn(
                  "rounded-lg px-3 py-2 text-sm",
                  isActive ? "bg-primary/15 text-primary" : "text-muted hover:text-primary",
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-8">
        {missingTable && (
          <p className="mb-6 rounded-lg border border-urgent/40 bg-urgent/10 px-4 py-3 text-sm text-urgent">
            Falta a tabela de conteúdo. No Supabase, SQL Editor, corre o ficheiro
            {" "}
            <code className="text-primary">supabase/migrations/002_site_content.sql</code>
            .
          </p>
        )}
        <Outlet />
      </main>

      {qrOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="QR WhatsApp"
        >
          <div className="w-full max-w-sm rounded-2xl border bg-surface p-6">
            <h2 className="font-heading text-lg font-semibold text-primary">Religar WhatsApp</h2>
            <p className="mt-1 text-sm text-muted">
              No telemóvel: WhatsApp → Dispositivos ligados → Ligar dispositivo. Se expirar, actualiza o QR.
            </p>
            <div className="mt-4 flex min-h-48 items-center justify-center rounded-xl border bg-background p-3">
              {qrLoading && <p className="text-sm text-muted">A obter QR…</p>}
              {!qrLoading && qrError && <p className="text-center text-sm text-urgent">{qrError}</p>}
              {!qrLoading && qrDataUrl && (
                <img src={qrDataUrl} alt="QR Code WhatsApp" className="h-52 w-52 object-contain" />
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => void openQr()}>
                Actualizar QR
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setQrOpen(false);
                  void refreshWa();
                }}
              >
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
